import { Component, Input, Output, EventEmitter, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslocoModule } from '@jsverse/transloco';

import { RegistrationService } from '../../../services/registration.service';

export interface AccountData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  agreedToTerms: boolean;
  agreedToPrivacy: boolean;
  preferredLanguage?: string;
}

export interface TargetCompany {
  id: string;
  name: string;
}

@Component({
  selector: 'app-register-join',
  templateUrl: './register-join.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
  ],
})
export class RegisterJoinComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Services
  private registrationService = inject(RegistrationService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  // Inputs/Outputs
  @Input() accountData!: AccountData;
  @Input() targetCompany!: TargetCompany;
  @Output() registrationComplete = new EventEmitter<void>();

  // State
  isLoading = false;

  // Form
  joinForm = new FormGroup({
    message: new FormControl('', [Validators.maxLength(500)]),
  });

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    if (!this.joinForm.valid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;

    const requestData = {
      email: this.accountData.email,
      firstName: this.accountData.firstName,
      lastName: this.accountData.lastName,
      companyId: this.targetCompany.id,
      message: this.joinForm.value.message || undefined,
      preferredLanguage: this.accountData.preferredLanguage,
    };

    this.registrationService
      .requestAccess(requestData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;

          // Store data for confirmation page
          localStorage.setItem('accessRequestCompanyName', this.targetCompany.name);
          localStorage.setItem('accessRequestEmail', this.accountData.email);

          // Emit completion for parent component
          this.registrationComplete.emit();

          // Redirect to access request sent confirmation page
          this.router.navigate(['/auth/access-request-sent']);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Access request failed:', error);
          const errorMessage = error.userFriendlyMessage || 'Failed to send access request. Please try again.';
          this.notificationService.error(errorMessage);
        },
      });
  }

  // Helper methods
  private markFormGroupTouched(): void {
    Object.keys(this.joinForm.controls).forEach((key) => {
      const control = this.joinForm.get(key);
      control?.markAsTouched();
    });
  }

  getCharacterCount(): number {
    return this.joinForm.get('message')?.value?.length || 0;
  }

  getRemainingCharacters(): number {
    return 500 - this.getCharacterCount();
  }

  goToLogin(): void {
    this.router.navigate(['/auth/login']);
  }
}